import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import type {
  subsidy_SubsidyReportItemResponse,
  subsidy_SubsidyReportPayloadResponse,
  subsidy_SubsidyReportSheetFieldResponse,
  subsidy_SubsidyReportSheetResponse,
} from '@/api';

// Sozialdepartement work queue (#2003): workflow items of category
// subsidy_report. The server returns the copy-ready sheet in its own order
// (one Go constant); the client only translates the labels.

export type SubsidyReportSheet = subsidy_SubsidyReportSheetResponse;
export type SubsidyReportSheetField = subsidy_SubsidyReportSheetFieldResponse;
export type SubsidyReportItem = subsidy_SubsidyReportItemResponse;
export type SubsidyReportPayload = subsidy_SubsidyReportPayloadResponse;

export type SubsidyReportKind = 'antrag' | 'mutation' | 'austritt' | 'kmbb';

/** The kinds the office raises with the manual button; kmbb comes from the KmbB flag on the contract (#2007). */
export const SUBSIDY_REPORT_MANUAL_KINDS = ['antrag', 'mutation', 'austritt'] as const;
export type SubsidyReportManualKind = (typeof SUBSIDY_REPORT_MANUAL_KINDS)[number];

export const SUBSIDY_REPORT_KIND_LABELS: Record<SubsidyReportKind, MessageDescriptor> = {
  antrag: msg({ id: 'subsidyReport.kind.antrag', message: 'Subsidy request' }),
  mutation: msg({ id: 'subsidyReport.kind.mutation', message: 'Mutation' }),
  austritt: msg({ id: 'subsidyReport.kind.austritt', message: 'Exit' }),
  kmbb: msg({ id: 'subsidyReport.kind.kmbb', message: 'KmbB request' }),
};

export function subsidyReportKindLabel(i18n: { _: (d: MessageDescriptor) => string }, kind?: string): string {
  const descriptor = kind ? SUBSIDY_REPORT_KIND_LABELS[kind as SubsidyReportKind] : undefined;
  return descriptor ? i18n._(descriptor) : (kind ?? '');
}

/** Labels of the sheet fields, keyed by the server's field key. */
export const SUBSIDY_REPORT_SHEET_LABELS: Record<string, MessageDescriptor> = {
  child_name: msg({ id: 'subsidyReport.sheet.child_name', message: 'Child' }),
  child_birthdate: msg({ id: 'subsidyReport.sheet.child_birthdate', message: 'Date of birth' }),
  household_address: msg({ id: 'subsidyReport.sheet.household_address', message: 'Home address' }),
  parents: msg({ id: 'subsidyReport.sheet.parents', message: 'Parents' }),
  payer_contacts: msg({ id: 'subsidyReport.sheet.payer_contacts', message: 'Invoice recipients' }),
  pid: msg({ id: 'subsidyReport.sheet.pid', message: 'PID' }),
  beitragsfaktor: msg({ id: 'subsidyReport.sheet.beitragsfaktor', message: 'Beitragsfaktor (BF)' }),
  beitragsfaktor_valid_from: msg({ id: 'subsidyReport.sheet.beitragsfaktor_valid_from', message: 'BF valid from' }),
  beitragsfaktor_valid_until: msg({ id: 'subsidyReport.sheet.beitragsfaktor_valid_until', message: 'BF valid until' }),
  sbu_days: msg({ id: 'subsidyReport.sheet.sbu_days', message: 'SBU days per week' }),
  sbu_valid_until: msg({ id: 'subsidyReport.sheet.sbu_valid_until', message: 'SBU valid until' }),
  offer_monday: msg({ id: 'subsidyReport.sheet.offer_monday', message: 'Care type Monday' }),
  offer_tuesday: msg({ id: 'subsidyReport.sheet.offer_tuesday', message: 'Care type Tuesday' }),
  offer_wednesday: msg({ id: 'subsidyReport.sheet.offer_wednesday', message: 'Care type Wednesday' }),
  offer_thursday: msg({ id: 'subsidyReport.sheet.offer_thursday', message: 'Care type Thursday' }),
  offer_friday: msg({ id: 'subsidyReport.sheet.offer_friday', message: 'Care type Friday' }),
  entry_date: msg({ id: 'subsidyReport.sheet.entry_date', message: 'Entry date' }),
  planned_exit_date: msg({ id: 'subsidyReport.sheet.planned_exit_date', message: 'Planned exit date' }),
  kmbb: msg({ id: 'subsidyReport.sheet.kmbb', message: 'KmbB' }),
  location: msg({ id: 'subsidyReport.sheet.location', message: 'Location' }),
};

export function subsidyReportSheetLabel(
  i18n: { _: (d: MessageDescriptor) => string },
  field: Pick<SubsidyReportSheetField, 'key'>,
): string {
  const descriptor = SUBSIDY_REPORT_SHEET_LABELS[field.key];
  return descriptor ? i18n._(descriptor) : field.key;
}

/** Labels of the mutation diff fields (models.SubsidyReportDiff*). */
export const SUBSIDY_REPORT_DIFF_LABELS: Record<string, MessageDescriptor> = {
  services: msg({ id: 'subsidyReport.diff.services', message: 'Services' }),
  weekdays: msg({ id: 'subsidyReport.diff.weekdays', message: 'Weekdays' }),
  sbu_days: msg({ id: 'subsidyReport.diff.sbu_days', message: 'SBU days per week' }),
  beitragsfaktor: msg({ id: 'subsidyReport.diff.beitragsfaktor', message: 'Beitragsfaktor (BF)' }),
  beitragsfaktor_valid_from: msg({ id: 'subsidyReport.diff.beitragsfaktor_valid_from', message: 'BF valid from' }),
  beitragsfaktor_valid_until: msg({ id: 'subsidyReport.diff.beitragsfaktor_valid_until', message: 'BF valid until' }),
  sbu_valid_until: msg({ id: 'subsidyReport.diff.sbu_valid_until', message: 'SBU valid until' }),
  location: msg({ id: 'subsidyReport.diff.location', message: 'Location' }),
};

/**
 * "Copy all": one `label<TAB>value` line per field, in the server's order. Tabs
 * and line breaks inside a value become spaces so each field stays one line.
 */
export function buildSubsidyReportCopyAll(
  fields: SubsidyReportSheetField[],
  labelOf: (field: SubsidyReportSheetField) => string,
): string {
  const clean = (s: string) => s.replace(/[\t\r\n]+/g, ' ');
  return fields.map((f) => `${clean(labelOf(f))}\t${clean(f.value)}`).join('\n');
}

/**
 * A rejected request leaves the version with a decision date, no decision
 * number and the invoice hold still on (#2003 D1).
 */
export function isSubsidyRequestRejected(contract: {
  subsidised?: boolean;
  subsidyDecisionOn?: string | null;
  subsidyDecisionNumber?: string | null;
  subsidyDecisionPending?: boolean;
}): boolean {
  return (
    !!contract.subsidised &&
    !!contract.subsidyDecisionOn &&
    !contract.subsidyDecisionNumber &&
    !!contract.subsidyDecisionPending
  );
}

/** The inbox line of an item: "<kind>: <child>". Reads the raw workflow payload. */
export function subsidyReportSummary(
  i18n: { _: (d: MessageDescriptor) => string },
  payload: Record<string, unknown> | undefined,
): string {
  const kind = subsidyReportKindLabel(i18n, typeof payload?.kind === 'string' ? payload.kind : undefined);
  const child = typeof payload?.child_name === 'string' ? payload.child_name : '';
  return child ? `${kind}: ${child}` : kind;
}
